import wvdefault, { Core, WebViewerInstance } from '@pdftron/webviewer';
import webviewerManifest from '@pdftron/webviewer/package.json';
import { immutableJSONPatch } from 'immutable-json-patch';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuthCache } from 'api/cache';
import { isUserAdmin, isUserTenantAdmin } from 'api/endpoints/users';
import {
  ReadingCacheType,
  useReadAPIWithServices,
} from 'api/useAPIWithServices';
import {
  NewPageData,
  WebViewerEventHandlers,
} from 'components/organisms/reusableWebviewer/types';
import { useAppServices } from 'container/depContext';
import { ID } from 'features/indexing/pages/execution/types';
import { UseWebviewerPageIDKey } from 'features/indexing/pages/execution/useWebViewer';
import { useStateChangeEvents } from 'hooks/useStateChangeEvents';
import { ApplicationError } from 'types/errorHandling';
import { fixDefaultCjsImport } from 'utils/fixDefaultCjsImport';

import injectedStyleUrl from './styles.scss?url';

const WebViewer = fixDefaultCjsImport(wvdefault);

const { REACT_APP_PDFTRON_LICENSE_KEY: license } = import.meta.env;

type WebviewerContextType = null | {
  instance: WebViewerInstance;
  deployViewer: (newParent: HTMLElement) => void;
  returnViewer: () => void;
};

type ListenerTuple = [
  /**
   * these names must be a subset of the events listed at https://sdk.apryse.com/api/web/Core.DocumentViewer.html
   * ideally this'd be `Parameters<Core.documentViewer.on>[0]` but typescript can't handle overloading like that
   */
  'pageNumberUpdated' | 'pagesUpdated' | 'documentLoaded' | 'documentUnloaded',
  (...args: any[]) => void,
];

const WebviewerContext = createContext<WebviewerContextType>(null);

export const useWebviewerContext = () => useContext(WebviewerContext);

const InvocationCancel = Symbol(
  `there's a new update that supercedes this; abort this invocation`
);

function routeWindowError(func: () => void) {
  let err;
  const listener = (event: ErrorEvent) => {
    event.preventDefault();
    err = event.error;
  };

  window.addEventListener('error', listener);
  try {
    func();
  } finally {
    window.removeEventListener('error', listener);
  }
  throw err;
}

export function useWebviewerInstance(
  viewerElementRef: React.RefObject<HTMLDivElement>
) {
  const [instance, setInstance] = useState<WebViewerInstance>();

  // manage instance lifecycle
  useEffect(() => {
    const controller = new AbortController();

    WebViewer(
      {
        path: `/WebViewer/lib/${webviewerManifest.version}`,
        fullAPI: true,
        licenseKey: license,
        preloadWorker: 'pdf',
        loadAsPDF: true,
        webviewerServerRangeRequests: false,
        css: injectedStyleUrl,
        enableRedaction: true,
      },
      viewerElementRef.current!
    ).then(async (instance) => {
      const cleanup = async () => {
        await instance.UI.dispose();
      };
      if (controller.signal.aborted) {
        return cleanup();
      }
      controller.signal.addEventListener('abort', cleanup);

      const pdfNetInitialize = instance.Core.PDFNet.initialize();

      instance.UI.importModularComponents(
        immutableJSONPatch(instance.UI.exportModularComponents(), [
          { op: 'remove', path: '/flyouts/MainMenuFlyout/items/3' },
        ])
      );

      await Promise.all([pdfNetInitialize]);
      setInstance(instance);
    });

    return () => {
      controller.abort();
    };
  }, [viewerElementRef]);

  // configure instance user data
  const { getUserRepo } = useAppServices();
  const { getCurrentUserInfo } = getUserRepo();
  const { loggedIn } = useAuthCache();
  const { data: userInfo } = useReadAPIWithServices(
    [loggedIn ? getCurrentUserInfo.name : null],
    getCurrentUserInfo,
    { cacheType: ReadingCacheType.ConfigurationDependency }
  );
  useEffect(() => {
    if (!instance) return;

    const { annotationManager } = instance.Core;

    if (!userInfo) {
      annotationManager.setCurrentUser('External user');
      return;
    }

    annotationManager.setCurrentUser(
      `${userInfo.firstName} ${userInfo.lastName}: ${userInfo.email}`
    );

    if (isUserAdmin(userInfo) || isUserTenantAdmin(userInfo))
      annotationManager.promoteUserToAdmin();
  }, [instance, userInfo]);

  return instance;
}

export function useWebViewerDocumentState(
  webViewerInstance: WebViewerInstance | undefined,
  eventHandlers?: Partial<WebViewerEventHandlers>
) {
  const [currentPageNumber, _setCurrentPageNumber] = useState(0);
  const [pageCount, _setPageCount] = useState(0);
  const [viewerDocument, _setViewerDocument] = useState<Core.Document>();
  const [currentPageId, _setCurrentPageId] = useState<ID>();
  const ref = useRef<AbortController>(new AbortController());
  useEffect(() => {
    if (!webViewerInstance) return;

    const { documentViewer } = webViewerInstance.Core;

    const updatePageData = async (): Promise<NewPageData> => {
      ref.current.abort();

      const controller = new AbortController();
      ref.current = controller;

      const doc = documentViewer.getDocument();

      if (!doc) {
        _setCurrentPageId(undefined);
        _setCurrentPageNumber(0);
        throw new ApplicationError(
          'failed to get PDFTron document',
          'failed to get PDFTron document'
        );
      }

      const pageNumber = documentViewer.getCurrentPage();

      const coreDoc = await doc.getPDFDoc();
      const page = await coreDoc.getPage(pageNumber);

      if (controller.signal.aborted) throw InvocationCancel;

      if (!page)
        throw new ApplicationError(
          `failed to get page #${pageNumber}`,
          `failed to get page #${pageNumber}`
        );

      const dict = await page.getSDFObj();
      if (controller.signal.aborted) throw InvocationCancel;

      const pageCount = await coreDoc.getPageCount();

      const existingPageIDKey = await dict.findObj(UseWebviewerPageIDKey);
      if (controller.signal.aborted) throw InvocationCancel;
      if (existingPageIDKey) {
        const existingPageId = (await existingPageIDKey.getAsPDFText()) as ID;
        _setCurrentPageId(existingPageId);
        _setCurrentPageNumber(pageNumber);
        return {
          pageNumber: pageNumber,
          pageId: existingPageId,
          pageCount: pageCount,
        };
      }

      const newId = crypto.randomUUID();

      await dict.putString(UseWebviewerPageIDKey, newId);
      if (controller.signal.aborted) throw InvocationCancel;

      return {
        pageNumber: pageNumber,
        pageId: newId,
        pageCount: pageCount,
      };
    };

    const handleNewPage = ({ pageNumber, pageId, pageCount }: NewPageData) => {
      if (eventHandlers?.onPageNumberUpdated) {
        eventHandlers.onPageNumberUpdated(
          pageNumber,
          pageId,
          pageCount,
          undefined
        );
      }
      _setCurrentPageId(pageId);
      _setCurrentPageNumber(pageNumber);
    };

    const pageChangeHandler = () => {
      updatePageData()
        .then(handleNewPage)
        .catch((error) => {
          if (error === InvocationCancel) return;

          const wrapped = ApplicationError.FromError(
            error,
            'exception getting PDF page'
          );

          if (eventHandlers?.onPageNumberUpdated)
            eventHandlers.onPageNumberUpdated(0, '' as ID, 0, wrapped);
          else wrapped.print();
        });
    };

    const addOrUpdateHandler = () => {
      const doc = documentViewer.getDocument();

      _setPageCount(doc?.getPageCount() ?? 0);
      _setViewerDocument(doc);
      pageChangeHandler();
    };

    const documentViewerListeners: ListenerTuple[] = [
      ['pageNumberUpdated', pageChangeHandler],
      ['pagesUpdated', addOrUpdateHandler],
      ['documentLoaded', addOrUpdateHandler],
      [
        'documentUnloaded',
        () => {
          _setPageCount(0);
          _setCurrentPageId(undefined);
          _setCurrentPageNumber(0);
          _setViewerDocument(undefined);
        },
      ],
    ];

    documentViewerListeners.forEach(([eventName, listener]) =>
      documentViewer.addEventListener(eventName, listener)
    );

    return () => {
      documentViewerListeners.forEach(([eventName, listener]) =>
        documentViewer.removeEventListener(eventName, listener)
      );
    };
  }, [eventHandlers, webViewerInstance]);

  const et = useStateChangeEvents(currentPageNumber);

  const setCurrentPageNumber = useCallback(
    (pageNumber: number) => {
      if (!webViewerInstance)
        return Promise.reject('webviewer instance does not exist');
      const { documentViewer } = webViewerInstance.Core;

      return new Promise<number>((resolve, reject) => {
        if (documentViewer.getCurrentPage() === pageNumber)
          return resolve(pageNumber);

        et.addEventListener(
          'change',
          (event) =>
            pageNumber === event.detail
              ? resolve(pageNumber)
              : reject('updated to different page number than requested'),
          { once: true }
        );
        documentViewer.setCurrentPage(pageNumber, false);
      });
    },
    [et, webViewerInstance]
  );

  const resetViewerDocument = useCallback(async () => {
    if (!webViewerInstance) return;
    const { documentViewer } = webViewerInstance.Core;

    await documentViewer.closeDocument();
  }, [webViewerInstance]);

  return {
    currentPageNumber,
    setCurrentPageNumber,
    currentPageId,
    pageCount,
    viewerDocument,
    resetViewerDocument,
  };
}

export function WebviewerContextProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const viewerElementParentRef = useRef<HTMLDivElement>(null);
  const viewerElementRef = useRef<HTMLDivElement>(null);

  const instance = useWebviewerInstance(viewerElementRef);

  const deployViewer = useCallback((newParent: HTMLElement) => {
    const viewerElement = viewerElementRef.current;
    if (viewerElement === null) throw new Error('viewer does not exist');

    try {
      routeWindowError(() => newParent.appendChild(viewerElement));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotSupportedError') {
        // expected error due to connectedCallback failing to reattach shadow root (it's still attached); do nothing
      } else if (e === undefined) {
      } else throw e;
    }
  }, []);

  const returnViewer = useCallback(() => {
    if (viewerElementParentRef.current === null)
      throw new Error('viewer parent does not exist');
    deployViewer(viewerElementParentRef.current);

    instance?.UI.closeDocument();
  }, [deployViewer, instance]);

  return (
    <WebviewerContext.Provider
      value={
        instance
          ? {
              instance,
              deployViewer,
              returnViewer,
            }
          : null
      }
    >
      <div
        ref={viewerElementParentRef}
        style={{ height: 0, width: 0, overflow: 'hidden' }}
      >
        <div
          ref={viewerElementRef}
          data-annot="viewerElement"
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      {children}
    </WebviewerContext.Provider>
  );
}
